import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import DatePicker from "@/components/DatePicker";
import { Settings, PlayCircle, RotateCcw, Database, Save, Sparkles, CalendarDays } from "lucide-react";

export default function Admin() {
  const qc = useQueryClient();
  const { data: cfg } = useQuery({ queryKey: ["config"], queryFn: async () => (await api.get("/config")).data });
  const { data: connectors = [] } = useQuery({ queryKey: ["connectors"], queryFn: async () => (await api.get("/connectors")).data });
  const { data: jobs = [] } = useQuery({ queryKey: ["jobs"], queryFn: async () => (await api.get("/import/jobs")).data });
  const { data: scans = [] } = useQuery({ queryKey: ["scans"], queryFn: async () => (await api.get("/scan/runs")).data });
  const { data: schedule = [] } = useQuery({ queryKey: ["schedule"], queryFn: async () => (await api.get("/schedule")).data });
  const [form, setForm] = useState(null);
  const [running, setRunning] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [sched, setSched] = useState({ auction_house: "Bonhams", sale_name: "", auction_type: "Evening Sale", sale_location: "London", date: "" });

  useEffect(() => { if (cfg && !form) setForm({ price_cap: cfg.price_cap, price_basis: cfg.price_basis, include_photographs: cfg.include_photographs, include_withdrawn: cfg.include_withdrawn, include_unsold: cfg.include_unsold }); }, [cfg, form]);

  const save = async () => {
    try {
      await api.put("/config", form);
      toast.success("Configuration saved. Re-run imports to apply scope changes.");
      qc.invalidateQueries({ queryKey: ["config"] });
    } catch { toast.error("Save failed (admin only)"); }
  };
  const runImport = async (house, dry) => {
    setRunning(true);
    try {
      const res = await api.post("/import/run", { house, dry_run: dry });
      const r = res.data.results.reduce((a, x) => ({ inserted: a.inserted + x.inserted, updated: a.updated + x.updated, unchanged: a.unchanged + x.unchanged, review: a.review + x.review_flagged }), { inserted: 0, updated: 0, unchanged: 0, review: 0 });
      toast.success(`${dry ? "Dry-run" : "Import"}: +${r.inserted} new, ${r.updated} updated, ${r.unchanged} unchanged, ${r.review} flagged`);
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["quality"] });
    } catch { toast.error("Import failed (admin only)"); }
    finally { setRunning(false); }
  };
  const reset = async () => { await api.post("/config/reset"); toast.success("Config reset to defaults"); qc.invalidateQueries({ queryKey: ["config"] }); setForm(null); };

  const addSchedule = async () => {
    if (!sched.sale_name || !sched.date) { toast.error("Sale name and date are required"); return; }
    try {
      await api.post("/schedule", sched);
      toast.success("Sale date added");
      setSched({ ...sched, sale_name: "", date: "" });
      qc.invalidateQueries({ queryKey: ["schedule"] });
    } catch { toast.error("Could not add (admin only)"); }
  };
  const delSchedule = async (sid) => {
    await api.delete(`/schedule/${sid}`);
    qc.invalidateQueries({ queryKey: ["schedule"] });
  };

  const runScan = async () => {
    setScanning(true);
    toast.info("AI scan started — searching the web for the previous day's verified results…");
    try {
      const res = await api.post("/scan/run", {});
      const s = res.data;
      if (s.errors?.length) toast.error(`Scan finished with issues: ${s.errors[0]}`);
      else toast.success(`Scan complete: ${s.candidates} candidate(s) found, ${s.inserted} new → review queue`);
      qc.invalidateQueries({ queryKey: ["scans"] });
      qc.invalidateQueries({ queryKey: ["review-queue"] });
      qc.invalidateQueries({ queryKey: ["quality"] });
    } catch { toast.error("Scan failed (admin only)"); }
    finally { setScanning(false); }
  };

  if (!form) return null;

  return (
    <div>
      <p className="text-[11px] font-mono uppercase tracking-widest text-amber-400/90">Administration</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
        <Settings className="w-7 h-7 text-amber-400" /> Admin & Imports
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
        {/* Config */}
        <div className="al-card p-6">
          <h3 className="font-serif text-xl mb-4">Global Configuration</h3>
          <p className="text-xs text-slate-500 mb-4">Single source of truth. All connectors and queries read these values.</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="al-label">Price cap (GBP)</label><input data-testid="admin-price-cap-input" type="number" className="al-input" value={form.price_cap} onChange={(e) => setForm({ ...form, price_cap: Number(e.target.value) })} /></div>
              <div><label className="al-label">Price basis</label>
                <select data-testid="admin-price-basis-select" className="al-input" value={form.price_basis} onChange={(e) => setForm({ ...form, price_basis: e.target.value })}>
                  <option value="hammer_price">Hammer price</option><option value="price_including_buyers_premium">Incl. buyer's premium</option>
                </select>
              </div>
            </div>
            {[["include_photographs", "Include photographs"], ["include_withdrawn", "Include withdrawn lots"], ["include_unsold", "Include unsold lots (any estimate)"]].map(([k, label]) => (
              <label key={k} className="flex items-center justify-between py-1 cursor-pointer">
                <span className="text-sm text-slate-300">{label}</span>
                <input data-testid={`admin-toggle-${k}`} type="checkbox" checked={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} className="w-4 h-4 accent-amber-500" />
              </label>
            ))}
            <div className="flex gap-2 pt-2">
              <button data-testid="admin-save-config" onClick={save} className="al-btn"><Save className="w-4 h-4" /> Save config</button>
              <button data-testid="admin-reset-config" onClick={reset} className="al-btn-ghost"><RotateCcw className="w-4 h-4" /> Reset</button>
            </div>
          </div>
        </div>

        {/* Imports & connectors */}
        <div className="al-card p-6">
          <h3 className="font-serif text-xl mb-4 flex items-center gap-2"><Database className="w-5 h-5 text-amber-400" /> Connectors & Imports</h3>
          <div className="flex gap-2 mb-4">
            <button data-testid="import-all-button" disabled={running} onClick={() => runImport("all", false)} className="al-btn"><PlayCircle className="w-4 h-4" /> Run all</button>
            <button data-testid="import-dryrun-button" disabled={running} onClick={() => runImport("all", true)} className="al-btn-ghost">Dry-run all</button>
          </div>
          <div className="space-y-2">
            {connectors.map((c) => (
              <div key={c.house} data-testid={`connector-${c.house.replace(/[^a-z]/gi, "").toLowerCase()}`} className="flex items-center justify-between p-3 rounded-md border border-slate-800 bg-[#0E131F]">
                <div>
                  <p className="text-sm text-slate-200">{c.house} <span className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80 ml-1">{c.method}</span></p>
                  <p className="text-[11px] text-slate-500 max-w-xs">{c.note}</p>
                </div>
                <button data-testid={`import-${c.house.replace(/[^a-z]/gi, "").toLowerCase()}`} disabled={running} onClick={() => runImport(c.house, false)} className="al-btn-ghost text-xs">Import</button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI Daily Market Scan */}
      <div className="al-card p-6 mt-5" data-testid="scan-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-serif text-xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-cyan-400" /> AI Daily Market Scan</h3>
          <button data-testid="scan-run-button" disabled={scanning} onClick={runScan} className="al-btn">
            <Sparkles className="w-4 h-4" /> {scanning ? "Scanning…" : "Run scan now"}
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-2 max-w-3xl">
          Runs automatically every day at <span className="text-amber-400 font-mono">7:00pm UK</span>. A web-grounded AI looks up the previous auction
          day's <span className="text-slate-200">publicly-reported</span> results for your tracked artists across all four houses and files them as
          <span className="text-cyan-300"> unverified candidates</span> in the Review Queue — with a source link for Pip to confirm. Nothing enters the
          verified dataset until a reviewer approves it. The AI is instructed never to invent prices (it returns nothing rather than guessing).
        </p>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-left text-sm" data-testid="scan-runs-table">
            <thead><tr className="border-y border-slate-800 bg-[#0E131F] text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <th className="px-4 py-2">Target Day</th><th className="px-4 py-2">Candidates</th><th className="px-4 py-2">New</th><th className="px-4 py-2">Errors</th><th className="px-4 py-2">Finished</th>
            </tr></thead>
            <tbody>
              {scans.map((s) => (
                <tr key={s.run_id} className="border-b border-slate-800/50 mono-num text-xs">
                  <td className="px-4 py-2.5 text-slate-200">{s.target_date}</td>
                  <td className="px-4 py-2.5 text-cyan-400">{s.candidates}</td>
                  <td className="px-4 py-2.5 text-emerald-400">{s.inserted}</td>
                  <td className="px-4 py-2.5 text-red-400">{s.errors?.length || 0}</td>
                  <td className="px-4 py-2.5 text-slate-500">{s.finished_at ? new Date(s.finished_at).toLocaleString("en-GB") : "—"}</td>
                </tr>
              ))}
              {scans.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No scans yet — run one or wait for 7pm UK.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Auction calendar management */}
      <div className="al-card p-6 mt-5" data-testid="calendar-panel">
        <h3 className="font-serif text-xl mb-4 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-amber-400" /> Auction Calendar</h3>
        <p className="text-xs text-slate-400 mb-4 max-w-3xl">Set when each house holds its sales and the type. The Auction Houses page shows these to everyone and <span className="text-slate-200">auto-labels each as Upcoming / This week / Past based on today's date.</span></p>
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 mb-4">
          <select data-testid="sched-house" className="al-input" value={sched.auction_house} onChange={(e) => setSched({ ...sched, auction_house: e.target.value })}>
            {["Bonhams", "Christie's", "Sotheby's", "Phillips"].map((h) => <option key={h}>{h}</option>)}
          </select>
          <input data-testid="sched-name" className="al-input sm:col-span-2" placeholder="Sale name" value={sched.sale_name} onChange={(e) => setSched({ ...sched, sale_name: e.target.value })} />
          <select data-testid="sched-type" className="al-input" value={sched.auction_type} onChange={(e) => setSched({ ...sched, auction_type: e.target.value })}>
            {["Evening Sale", "Day Sale", "Online Sale", "Private Sale"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <select data-testid="sched-location" className="al-input" value={sched.sale_location} onChange={(e) => setSched({ ...sched, sale_location: e.target.value })}>
            {["London", "New York"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <DatePicker testid="sched-date" value={sched.date} onChange={(d) => setSched({ ...sched, date: d })} />
        </div>
        <button data-testid="sched-add" onClick={addSchedule} className="al-btn mb-4"><CalendarDays className="w-4 h-4" /> Add sale date</button>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="schedule-table">
            <thead><tr className="border-y border-slate-800 bg-[#0E131F] text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <th className="px-4 py-2">House</th><th className="px-4 py-2">Sale</th><th className="px-4 py-2">Type</th><th className="px-4 py-2">City</th><th className="px-4 py-2">Date</th><th className="px-4 py-2">Status</th><th className="px-4 py-2"></th>
            </tr></thead>
            <tbody>
              {schedule.map((s) => (
                <tr key={s.schedule_id} className="border-b border-slate-800/50 text-xs">
                  <td className="px-4 py-2.5 text-slate-200">{s.auction_house}</td>
                  <td className="px-4 py-2.5 text-slate-300">{s.sale_name}</td>
                  <td className="px-4 py-2.5 text-slate-400">{s.auction_type}</td>
                  <td className="px-4 py-2.5 text-slate-400">{s.sale_location}</td>
                  <td className="px-4 py-2.5 mono-num text-slate-400">{s.date}</td>
                  <td className="px-4 py-2.5 text-slate-300">{s.status}</td>
                  <td className="px-4 py-2.5 text-right"><button data-testid={`sched-del-${s.schedule_id}`} onClick={() => delSchedule(s.schedule_id)} className="text-slate-500 hover:text-red-400">Remove</button></td>
                </tr>
              ))}
              {schedule.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">No sale dates yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Jobs */}
      <div className="al-card mt-5 overflow-hidden">
        <p className="al-label p-4 pb-2">Recent Import Jobs</p>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="jobs-table">
            <thead><tr className="border-y border-slate-800 bg-[#0E131F] text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <th className="px-4 py-2">House</th><th className="px-4 py-2">Mode</th><th className="px-4 py-2">Inserted</th><th className="px-4 py-2">Updated</th><th className="px-4 py-2">Unchanged</th><th className="px-4 py-2">Excluded</th><th className="px-4 py-2">Flagged</th><th className="px-4 py-2">Errors</th><th className="px-4 py-2">Finished</th>
            </tr></thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.job_id} className="border-b border-slate-800/50 mono-num text-xs">
                  <td className="px-4 py-2.5 text-slate-200">{j.house}</td>
                  <td className="px-4 py-2.5">{j.dry_run ? "dry-run" : "live"}</td>
                  <td className="px-4 py-2.5 text-emerald-400">{j.inserted}</td>
                  <td className="px-4 py-2.5 text-cyan-400">{j.updated}</td>
                  <td className="px-4 py-2.5 text-slate-500">{j.unchanged}</td>
                  <td className="px-4 py-2.5 text-slate-400">{j.excluded}</td>
                  <td className="px-4 py-2.5 text-violet-400">{j.review_flagged}</td>
                  <td className="px-4 py-2.5 text-red-400">{j.errors?.length || 0}</td>
                  <td className="px-4 py-2.5 text-slate-500">{j.finished_at ? new Date(j.finished_at).toLocaleString("en-GB") : "—"}</td>
                </tr>
              ))}
              {jobs.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">No jobs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
